import React, { useEffect, useState, useCallback } from 'react';
import AppBar from '@mui/material/AppBar';
import { Link } from 'react-router';
import Toolbar from '@mui/material/Toolbar';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Container from '@mui/material/Container';
import LoadingOverlay from '../../loadingOverlay/LoadingOverlay';
import InboxPreviewDialog from '../dialogs/InboxPreviewDialog';
import Picker from '../../picker/Picker';
import useApi from '../../hooks/useApi';
import useSnackbar from '../../hooks/useSnackbar';
import ShoppingList from '../../editor/ShoppingList';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormHelperText from '@mui/material/FormHelperText';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import InputLabel from '@mui/material/InputLabel';
import Alert from '@mui/material/Alert';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InputAdornment from '@mui/material/InputAdornment';
import UnauthenticatedDialog from '../dialogs/UnauthenticatedDialog';
import useSuspenseFetch from '../../hooks/useSuspenseFetch';

const Setup = ({ email, onUpdateEmail, onNext, onBack, onFetchTopics, drawerWidth, steps, step, campaign, isProofSent, audienceName, titleBarDepth, isTemplate = false, setStep, type = 'email' }) => {
    const { data: account } = useSuspenseFetch('account');
    const { smtpUsername, safeSenders } = account;
    const isAuthenticated = account.mxCheck && account.dkim && account.dmarc && account.spf;
    const { data: topics } = useSuspenseFetch('topics');
    const { handlePut } = useApi();
    const { showSnackbar } = useSnackbar();

    const [isInitialising, setisInitialising] = useState(true);
    const [showInboxPreview, setShowInboxPreview] = useState(false);
    const [focusFromEmail, setFocusFromEmail] = useState(false);
    const [isValid, setIsValid] = useState(false);
    const [form, setForm] = useState(null);
    const [fromEmailField, setFromEmailField] = useState(null);
    const [isFromEmailValid, setIsFromEmailValid] = useState();

    const [showUnsafeSendersField, setShowUnsafeSendersField] = useState(safeSenders.length === 0 || (email.fromName > '' && !safeSenders.includes(email.fromEmail)));
    const [safeSendersFromEmail, setSafeSendersFromEmail] = useState(() => {
        if (safeSenders.includes(email.fromEmail)) {
            return email.fromEmail;
        }
        else if (safeSenders.length > 0) {
            return safeSenders[0];
        }
        else {
            return 'other';
        }
    });

    const [subject, setSubject] = useState(type === 'page' ? email.title : email.subject);
    const [fromName, setFromName] = useState(email.fromName);
    const [fromEmail, setFromEmail] = useState(email.fromEmail);
    const [gaCampaign, setGaCampaign] = useState(email.gaCampaign);
    const [hasUserChosenOther, setHasUserChosenOther] = useState(false);
    const [hasInitializedSafeSender, setHasInitializedSafeSender] = useState(false);
    const [showUnauthenticatedDialog, setShowUnauthenticatedDialog] = useState(false);

    const handleUpdateEmailTopics = async (id, topicIds) => {
        const params = {
            autoSave: false,
            ...(topicIds && { topicIds })
        };

        const response = await handlePut(`emails/${id}`, params);

        if (!response.ok) {
            showSnackbar('Email or Topic not found', 'error');
        }
        else {
            onFetchTopics();
        }
    };

    const handleUpdatePageTopics = async (id, topicIds) => {
        const params = {
            ...email,
            autoSave: false,
            ...(topicIds && { topicIds })
        };

        const response = await handlePut(`pages/${id}`, params);

        if (!response.ok) {
            showSnackbar('Landing Page or Topic not found', 'error');
        }
        else {
            onFetchTopics();
        }
    };

    const handleUpdateTopics = async ({ id, topics = null }) => {
        const topicIds = topics?.map(e => e.id);

        if (type === 'page') {
            handleUpdatePageTopics(id, topicIds);
        }
        else {
            handleUpdateEmailTopics(id, topicIds);
        }
    };

    const onRefChange = useCallback(node => setForm(node), []);

    const onFromEmailFieldChange = useCallback(node => setFromEmailField(node), []);

    const handleShowInboxPreview = () => {
        setShowInboxPreview(true);
    };

    const handleSave = () => {
        if (type === 'page') {
            onUpdateEmail({
                title: subject,
                gaCampaign
            });
        }
        else {
            onUpdateEmail({
                subject,
                fromName,
                fromEmail: email.fromEmail || smtpUsername || fromEmail,
                gaCampaign
            });
        }
    };

    const proceedToStep3 = () => {
        onNext({
            subject,
            ...(type === 'page' && { title: subject }),
            fromName,
            fromEmail: smtpUsername || email.fromEmail || fromEmail,
            gaCampaign,
            name: email.name
        });
    };

    const handleNext = () => {
        handleSave();

        if (!isAuthenticated && !smtpUsername) {
            setShowUnauthenticatedDialog(true);
            return;
        }

        proceedToStep3();
    };

    const handleBack = () => {
        handleSave();

        onBack({
            subject,
            ...(type === 'page' && { title: subject }),
            fromName,
            fromEmail: smtpUsername || email.fromEmail || fromEmail,
            gaCampaign,
            name: email.name
        });
    };

    const handleCheckIsValid = () => {
        return (form && form.checkValidity());
    };

    const sanitizeLocalPart = (value) => {
        return value
            .trim()
            .replace(/["'<>()\\[\]\s]+/g, '')
            .replace(/[^a-zA-Z0-9._%+-]/g, '');
    };

    const handleSafeSenderChange = (event) => {
        const selected = event.target.value;
        setSafeSendersFromEmail(selected);

        if (selected !== 'other') {
            setHasUserChosenOther(false);
            setFromEmail(selected);
            onUpdateEmail({
                ...email,
                fromEmail: selected
            });
        }
        else {
            setHasUserChosenOther(true);
        }
    };

    const handleFromEmailChange = (e) => {
        const input = e.target.value;

        const rawDomain = isAuthenticated
            ? account?.sendingDomain || ''
            : 'newzapp.co.uk';

        const domainParts = rawDomain.split('.');
        const domain = domainParts.length > 1
            ? domainParts.slice(1).join('.')
            : rawDomain;

        const localPart = sanitizeLocalPart(input);
        const newValue = `${localPart}@${domain}`;

        setFromEmail(newValue);
        onUpdateEmail({ ...email, fromEmail: newValue });
    };

    useEffect(() => {
        setisInitialising(false);
    }, []);

    useEffect(() => {
        setIsValid(handleCheckIsValid());
    }, [form, subject, fromName, fromEmail, gaCampaign]);

    useEffect(() => {
        fromEmailField && setIsFromEmailValid(() => fromEmailField.checkValidity());
    }, [fromEmail]);

    useEffect(() => {
        if (isInitialising || hasUserChosenOther || hasInitializedSafeSender || !email || !safeSenders || !account) {
            return;
        }

        if (hasUserChosenOther) {
            return;
        }

        let newValue = 'other';

        if (!isAuthenticated) {
            newValue = 'other';
        }
        else if (email.fromEmail && safeSenders.includes(email.fromEmail)) {
            newValue = email.fromEmail;
        }
        else if (safeSenders.length > 0) {
            newValue = safeSenders[0];
        }

        setSafeSendersFromEmail(newValue);
        setHasInitializedSafeSender(true);
    }, [account, email, safeSenders, isAuthenticated, hasUserChosenOther, hasInitializedSafeSender, isInitialising]);

    useEffect(() => {
        if (!hasInitializedSafeSender) {
            return;
        }

        if (safeSendersFromEmail === 'other') {
            if (!isInitialising && email.fromEmail) {
                const localPart = email.fromEmail.split('@')[0];
                const domain = isAuthenticated
                    ? (account?.sendingDomain?.split('.').slice(1).join('.') || '')
                    : 'newzapp.co.uk';

                const updatedFromEmail = `${localPart}@${domain}`;
                setFocusFromEmail(true);
                setFromEmail(updatedFromEmail);
                onUpdateEmail({
                    ...email,
                    fromEmail: updatedFromEmail
                });
            }
            else {
                setFocusFromEmail(true);
                setFromEmail('');
            }
        }
        else {
            setFromEmail(safeSendersFromEmail);
        }
    }, [safeSendersFromEmail, isInitialising, hasInitializedSafeSender]);

    useEffect(() => {
        setShowUnsafeSendersField(safeSenders.length === 0 || safeSendersFromEmail === 'other');
    }, [safeSenders, safeSendersFromEmail, fromEmail]);

    useEffect(() => {
        if (!account?.company) {
            return;
        }

        const sanitize = (value) => value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

        const sanitizedCompany = sanitize(account.company);

        if (!isAuthenticated && smtpUsername === null) {
            const fallbackEmail = `no-reply-${sanitizedCompany}@newzapp.co.uk`;
            const preferredSafeSender = safeSendersFromEmail !== 'other' ? safeSendersFromEmail : fallbackEmail;

            if (fromEmail !== preferredSafeSender) {

                setFromEmail(preferredSafeSender);
                onUpdateEmail({
                    ...email,
                    fromEmail: preferredSafeSender
                });
            }
        }
        else if (isAuthenticated && account.sendingDomain && smtpUsername === null) {
            const isSafeSender = safeSenders.includes(email.fromEmail);

            if (!isSafeSender && email.fromEmail !== null) {
                setSafeSendersFromEmail('other');
                setFromEmail(email.fromEmail);
            }
            else if (safeSenders.length > 0 && email.fromEmail !== null) {
                const domainParts = account.sendingDomain.split('.');
                const validDomain = domainParts.length > 1
                    ? domainParts.slice(1).join('.')
                    : account.sendingDomain;

                const sanitizedName = sanitize(account.company || account.name);

                const authenticatedEmail = `${sanitizedName}@${validDomain}`;

                const isFallback = (fromEmail || '').startsWith('no-reply-');
                const isDifferent = fromEmail !== authenticatedEmail;

                if ((!fromEmail || isFallback) && isDifferent) {
                    setFromEmail(authenticatedEmail);
                    onUpdateEmail({
                        ...email,
                        fromEmail: authenticatedEmail
                    });
                }
            }
        }
    }, [account, isAuthenticated, safeSendersFromEmail]);

    return (
        <>
            <AppBar position="sticky" color="inherit" sx={{ top: 64, zIndex: titleBarDepth }}>
                <Toolbar>
                    <Typography component="h1" variant="h6">{type === 'email' ? 'Campaign' : 'Landing Page'} Setup</Typography>
                </Toolbar>
            </AppBar>
            <div style={{ marginRight: drawerWidth, flexGrow: 1, height: 'calc(100vh - 130px)', overflowY: 'auto' }}>
                {isInitialising ? <LoadingOverlay /> : (
                    <Container disableGutters>
                        <Paper sx={{ m: 2, p: 3 }}>
                            <Grid container spacing={2}>
                                {!smtpUsername && !isAuthenticated && (
                                    <Grid size={{ xs: 12 }}>
                                        <Alert severity="warning" icon={<WarningAmberIcon fontSize="inherit" />}>
                                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                                                Your sending domain has not been authenticated. Emails will currently be sent from <strong>{fromEmail}</strong> to improve inbox delivery. To send from your own domain&nbsp;
                                                <Typography
                                                    variant="body2"
                                                    component={Link}
                                                    to="/account/configuration"
                                                    sx={{ color: 'primary.main', cursor: 'pointer', fontWeight: 500, textDecoration: 'underline' }}
                                                >
                                                    click here to set up domain authentication
                                                </Typography>.
                                            </Typography>
                                        </Alert>
                                    </Grid>
                                )}
                                <Grid size={{ xs: 12 }}>
                                    <form ref={onRefChange}>
                                        <Grid container spacing={3} sx={{ alignItems: 'stretch' }}>
                                            <Grid size={{ xs: 12 }}>
                                                <TextField
                                                    required
                                                    fullWidth
                                                    value={subject}
                                                    onChange={e => setSubject(e.target.value)}
                                                    label={type === 'page' ? 'Title' : 'Subject line'}
                                                    margin="none"
                                                />
                                            </Grid>
                                            {type !== 'page' && (
                                                <Grid size={{ xs: 12 }}>
                                                    <TextField
                                                        required
                                                        fullWidth
                                                        value={fromName}
                                                        onChange={e => setFromName(e.target.value)}
                                                        label="Email from name"
                                                        margin="none"
                                                    />
                                                </Grid>
                                            )}
                                            {type !== 'page' && (
                                                <>
                                                    {smtpUsername ? (
                                                        <Grid size={{ xs: 12 }}>
                                                            <TextField
                                                                fullWidth
                                                                disabled
                                                                value={smtpUsername}
                                                                label="From email address"
                                                                margin="none"
                                                            />
                                                            <FormHelperText margin="dense">
                                                                You cannot change your from email address when using a custom SMTP configuration.
                                                            </FormHelperText>
                                                        </Grid>
                                                    ) : (
                                                        <>
                                                            <Grid size={{ xs: 12, lg: isAuthenticated ? 6 : 12 }}>
                                                                <Box
                                                                    sx={{
                                                                        display: 'flex',
                                                                        flexDirection: 'column',
                                                                        gap: 2
                                                                    }}
                                                                >
                                                                    {safeSenders.length > 0 && (
                                                                        <FormControl fullWidth margin="none">
                                                                            <InputLabel>From email address</InputLabel>
                                                                            <Select
                                                                                required
                                                                                value={!isAuthenticated ? 'other' : safeSendersFromEmail}
                                                                                onChange={handleSafeSenderChange}
                                                                                label="From email address"
                                                                                disabled={!isAuthenticated}
                                                                                displayEmpty
                                                                            >
                                                                                {safeSenders.map((emailAddress, i) => (
                                                                                    <MenuItem key={i} value={emailAddress}>
                                                                                        {emailAddress}
                                                                                    </MenuItem>
                                                                                ))}
                                                                                <MenuItem value="other">Enter address not on Safe Senders list</MenuItem>
                                                                            </Select>
                                                                            <FormHelperText margin="dense">
                                                                                To ensure email delivery, your from address should be on your organisation's Safe Senders list. Contact your account manager to add an address.
                                                                            </FormHelperText>
                                                                        </FormControl>
                                                                    )}

                                                                    {showUnsafeSendersField && (
                                                                        <Box sx={{ pl: safeSenders.length > 0 ? 1 : 0 }}>
                                                                            <TextField
                                                                                fullWidth
                                                                                autoFocus={focusFromEmail}
                                                                                type="text"
                                                                                error={isFromEmailValid === false}
                                                                                inputRef={onFromEmailFieldChange}
                                                                                required
                                                                                disabled={!isAuthenticated}
                                                                                value={fromEmail?.split('@')[0] || ''}
                                                                                onChange={handleFromEmailChange}
                                                                                label="From email address"
                                                                                margin="none"
                                                                                slotProps={{
                                                                                    input: {
                                                                                        endAdornment: (
                                                                                            <InputAdornment position="end" sx={{ color: 'text.secondary', fontWeight: 'bold' }}>
                                                                                                @{isAuthenticated
                                                                                                    ? (() => {
                                                                                                        const parts = (account?.sendingDomain || '').split('.');
                                                                                                        return parts.length > 1 ? parts.slice(1).join('.') : parts.join('.');
                                                                                                    })()
                                                                                                    : 'newzapp.co.uk'}
                                                                                            </InputAdornment>
                                                                                        )
                                                                                    }
                                                                                }}
                                                                            />
                                                                        </Box>
                                                                    )}
                                                                </Box>
                                                            </Grid>
                                                            {isAuthenticated && (
                                                                <Grid size={{ xs: 12, lg: 6 }} sx={{ display: 'flex' }}>
                                                                    <Alert
                                                                        severity="success"
                                                                        icon={<CheckCircleIcon fontSize="inherit" />}
                                                                        sx={{
                                                                            alignItems: 'center',
                                                                            display: 'flex',
                                                                            flex: 1
                                                                        }}
                                                                    >
                                                                        Your sending domain is fully authenticated.
                                                                    </Alert>
                                                                </Grid>
                                                            )}
                                                        </>
                                                    )}
                                                </>
                                            )}
                                            <Grid size={{ xs: 12 }}>
                                                <TextField
                                                    fullWidth
                                                    value={gaCampaign}
                                                    onChange={e => setGaCampaign(e.target.value)}
                                                    label="Google Analytics campaign"
                                                    margin="none"
                                                />
                                            </Grid>
                                            {!isTemplate && (
                                                <Grid size={{ xs: 12 }}>
                                                    <Box
                                                        sx={{
                                                            border: '1px solid rgba(0, 0, 0, 0.2)',
                                                            borderRadius: '4px',
                                                            py: 1
                                                        }}
                                                    >
                                                        <Table size="small">
                                                            <TableBody>
                                                                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                                                    <Picker
                                                                        availableItems={topics}
                                                                        selectedItems={email.topics}
                                                                        onChangeItems={topics => handleUpdateTopics({ id: email.id, topics })}
                                                                    />
                                                                </TableRow>
                                                            </TableBody>
                                                        </Table>
                                                    </Box>
                                                </Grid>
                                            )}
                                            {type === 'email' && (
                                                <Grid size={{ xs: 12 }}>
                                                    <Button onClick={handleShowInboxPreview}>Inbox Preview</Button>
                                                </Grid>
                                            )}
                                        </Grid>
                                    </form>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Container>
                )}
            </div>
            <ShoppingList
                drawerWidth={drawerWidth}
                stepNumber={step}
                steps={steps}
                disabled={!isValid}
                onNext={handleNext}
                onBack={handleBack}
                campaign={campaign}
                email={email}
                isProofSent={isProofSent}
                audienceName={audienceName}
                setStep={setStep}
            />
            {
                showInboxPreview && (
                    <InboxPreviewDialog
                        open={showInboxPreview}
                        onClose={() => setShowInboxPreview(false)}
                        fromName={fromName}
                        fromEmail={smtpUsername || fromEmail}
                        subject={subject}
                        html={email.html}
                    />
                )
            }
            {
                showUnauthenticatedDialog && (
                    <UnauthenticatedDialog
                        open={showUnauthenticatedDialog}
                        onCancel={() => setShowUnauthenticatedDialog(false)}
                        onConfirm={() => {
                            setShowUnauthenticatedDialog(false);
                            proceedToStep3();
                        }}
                        fromEmail={fromEmail}
                    />
                )
            }
        </>
    );
};

export default Setup;